// ProviderSignup.js - Caretaker signup page
// City field uses Google Places Autocomplete (cities only, US only).
// State auto-fills from the selected city.
// SERVED_CITIES check after Firestore write:
//   in-area  → /verify-email → onboarding (normal flow)
//   out-of-area → /out-of-area (saves outOfArea: true to Firestore)

import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Cities within our active service counties.
// Mahoning County OH + Trumbull County OH + Lawrence County PA.
// Keep in sync with Booking.js, Signup.js, and OutOfArea.js.
const SERVED_CITIES = [
  // Mahoning County, Ohio
  'youngstown', 'boardman', 'canfield', 'poland', 'struthers', 'campbell',
  'sebring', 'alliance', 'austintown', 'beaver', 'beloit', 'berlin center',
  'columbiana', 'craig beach', 'ellsworth', 'goshen', 'jackson', 'lake milton',
  'lowellville', 'new middletown', 'north lima', 'springfield',
  // Trumbull County, Ohio
  'warren', 'niles', 'cortland', 'girard', 'hubbard', 'brookfield',
  'newton falls', 'lordstown', 'bazetta', 'bloomfield', 'bristolville',
  'champion', 'farmington', 'fowler', 'kinsman', 'liberty', 'leavittsburg',
  'mcdonald', 'masury', 'mineral ridge', 'orwell', 'southington', 'vienna',
  'weathersfield',
  // Lawrence County, Pennsylvania
  'new castle', 'ellwood city', 'wampum', 'volant', 'enon valley',
  'edinburg', 'bessemer', 'neshannock', 'shenango', 'hickory',
  'north sewickley', 'pulaski', 'plain grove',
];

function ProviderSignup() {
  const navigate = useNavigate();

  // Form field state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // City/state — city is set by Places autocomplete, state auto-fills from it
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Refs for Google Places — attached to the city input DOM node
  const cityInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // ===== GOOGLE PLACES AUTOCOMPLETE =====
  // Loaded dynamically — same pattern as Booking.js and Signup.js
  useEffect(() => {
    const initAutocomplete = () => {
      if (!cityInputRef.current || !window.google) return;

      // Restrict to city-level results within the US only
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        cityInputRef.current,
        {
          types: ['(cities)'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components'],
        }
      );

      // When a city is chosen from the dropdown, extract city and state
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        let cityName = '';
        let stateName = '';

        place.address_components.forEach(component => {
          // locality = city name
          if (component.types.includes('locality')) {
            cityName = component.long_name;
          }
          // administrative_area_level_1 = state abbreviation (e.g. "OH")
          if (component.types.includes('administrative_area_level_1')) {
            stateName = component.short_name;
          }
        });

        // Store lowercase for Firestore and SERVED_CITIES comparison
        setCity(cityName.toLowerCase().trim());
        setState(stateName.toLowerCase().trim());

        // Keep the visible input showing just the city name
        if (cityInputRef.current) {
          cityInputRef.current.value = cityName;
        }
      });
    };

    const loadGooglePlaces = () => {
      // Already loaded — init immediately
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }
      // Script tag already injected by another page — attach to its onload
      const existingScript = document.getElementById('google-places-script');
      if (existingScript) {
        existingScript.onload = initAutocomplete;
        return;
      }
      // First load — inject the script
      const script = document.createElement('script');
      script.id = 'google-places-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    };

    loadGooglePlaces();

    // Clear autocomplete listeners on unmount to prevent memory leaks
    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // ===== PASSWORD CHECKS =====
  // All four must pass before signup is allowed
  const pwChecks = (pw) => ({
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(pw),
  });

  // ===== SIGNUP HANDLER =====
  const handleSignup = async () => {
    if (!firstName || !lastName || !email || !phone || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    // Ensure city was selected from the Places dropdown (not just typed)
    if (!city) {
      setError('Please select your city from the autocomplete dropdown');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Accepts formats: 724-555-0123, (724) 555-0123, 7245550123, +17245550123
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setError('Please enter a valid US phone number (e.g. 724-555-0123)');
      return;
    }

    const checks = pwChecks(password);
    if (!Object.values(checks).every(Boolean)) {
      setError('Password does not meet the strength requirements');
      return;
    }

    // Hoisted so the catch block can call user.delete() if Firestore fails
    let user = null;

    try {
      setLoading(true);
      setError('');

      // Step 1 — Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;

      // Step 2 — Check service area before writing to Firestore
      const isServed = SERVED_CITIES.includes(city.toLowerCase().trim());

      // Step 3 — Save provider profile to Firestore
      // serviceCity / serviceState used for job matching throughout the app
      // city / state used by OutOfArea.js to display the user's location
      // outOfArea flag drives the login redirect in Login.js
      // onboardingComplete: false triggers onboarding flow after login
      await setDoc(doc(db, 'users', user.uid), {
        firstName,
        lastName,
        email,
        phone,
        role:              'provider',
        city:              city.toLowerCase().trim(),
        state:             state.toLowerCase().trim(),
        serviceCity:       city.toLowerCase().trim(),
        serviceState:      state.toLowerCase().trim(),
        outOfArea:         !isServed,
        rating:            0,
        jobsCompleted:     0,
        onboardingComplete: false,
        createdAt:         new Date(),
      });

      // Step 4 — Route based on service area
      if (!isServed) {
        // Out-of-area: skip email verification and show the expansion request page
        navigate('/out-of-area');
      } else {
        // In-area: send verification email then go to verification gate
        // ProviderDashboard will redirect to /provider/onboarding after login
        try {
          await sendEmailVerification(user);
        } catch (verifyErr) {
          console.warn('Could not send verification email:', verifyErr.message);
        }
        navigate('/verify-email');
      }

    } catch (err) {
      // If Auth succeeded but Firestore failed, roll back the Auth account
      // so the user can try again without getting "email already in use"
      if (user) {
        try { await user.delete(); } catch { /* ignore — account may already be gone */ }
      }

      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <a href="/" className="auth-logo">Evermark Memorial</a>

      <div className="auth-card">
        <h2>Become a Caretaker</h2>
        <p className="auth-subtitle">
          Earn money by helping families honor their loved ones
        </p>

        {error && <div className="auth-error">{error}</div>}

        {/* ===== FIRST AND LAST NAME ===== */}
        <div className="auth-row">
          <div className="auth-field">
            <label>First Name</label>
            <input
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="auth-field">
            <label>Last Name</label>
            <input
              type="text"
              placeholder="Smith"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
            />
          </div>
        </div>

        {/* ===== EMAIL ===== */}
        <div className="auth-field">
          <label>Email Address</label>
          <input
            type="email"
            placeholder="john@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
          />
        </div>

        {/* ===== PHONE ===== */}
        <div className="auth-field">
          <label>Phone Number</label>
          <input
            type="tel"
            placeholder="e.g. 724-555-0123"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={20}
          />
        </div>

        {/* ===== CITY WITH GOOGLE PLACES AUTOCOMPLETE =====
            Uncontrolled input — Google Places writes directly to the DOM node.
            The city and state React state variables are set via place_changed. */}
        <div className="auth-row">
          <div className="auth-field">
            <label>Your City</label>
            <input
              ref={cityInputRef}
              type="text"
              placeholder="Start typing your city..."
              defaultValue=""
              className="auth-input-autocomplete"
            />
            <p className="autocomplete-hint">Select from the dropdown</p>
            {/* Helper text showing which counties are active */}
            <p className="provider-city-hint">
              We serve Mahoning &amp; Trumbull County, OH and Lawrence County, PA
            </p>
          </div>

          {/* State auto-fills when city is selected — read-only */}
          <div className="auth-field">
            <label>State</label>
            <input
              type="text"
              value={state ? state.toUpperCase() : ''}
              readOnly
              placeholder="Auto-filled"
              className="auth-input-readonly"
            />
          </div>
        </div>

        {/* ===== PASSWORD ===== */}
        <div className="auth-field">
          <label>Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={128}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(prev => !prev)}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {/* Password strength indicator */}
          {password && (
            <div className="pw-strength">
              {[
                { key: 'length',  label: 'At least 8 characters' },
                { key: 'upper',   label: 'One uppercase letter' },
                { key: 'number',  label: 'One number' },
                { key: 'special', label: 'One special character (!@#$…)' },
              ].map(({ key, label }) => {
                const pass = pwChecks(password)[key];
                return (
                  <div key={key} className={`pw-check ${pass ? 'pw-check-pass' : 'pw-check-fail'}`}>
                    <span>{pass ? '✓' : '✗'}</span> {label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ===== CONFIRM PASSWORD ===== */}
        <div className="auth-field">
          <label>Confirm Password</label>
          <input
            type="password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            maxLength={128}
          />
        </div>

        {/* How it works for providers */}
        <div className="provider-info-box">
          <h4>How it works for caretakers:</h4>
          <ul>
            <li>✓ Browse available jobs in your area</li>
            <li>✓ Claim jobs that fit your schedule</li>
            <li>✓ Visit the grave and submit before &amp; after photos</li>
            <li>✓ Get paid directly to your bank account</li>
          </ul>
        </div>

        <button
          className="btn-primary btn-full"
          onClick={handleSignup}
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Sign Up as Caretaker'}
        </button>

        {/* Terms agreement happens after email verification and onboarding —
            not at signup time. Links are available in the footer. */}
        <p className="auth-switch">
          Already have an account? <a href="/login">Log in</a>
        </p>
      </div>

    </div>
  );
}

export default ProviderSignup;
